import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { maybeShowTaskReview, notifyTaskReviewChanged } from "@/lib/taskReview";
import { ClipboardCheck, RefreshCcw, CalendarX2, Link2, Video, Copy } from "lucide-react";
import { WhatHappensNextPanel } from "@/components/WhatHappensNextPanel";
import { useSlotPicker } from "@/hooks/useSlotPicker";
import { SlotPickerView } from "@/components/SlotPickerView";

const INTENT_OPTIONS = ["high_intent", "hot", "warm", "cold", "unclear"];

/* Shared cancellation-reason picker (2026-09 auditability) — used by both
   the quick Cancel dialog and the Outcome dialog's Cancelled occurrence, so
   the "note required when Other" rule lives in exactly one place. Reasons
   come from GET /appointments/cancel-reasons (backend's own catalog),
   never hardcoded twice. */
const CancelReasonFields = ({ testPrefix, cancelReasons, reasonCode, setReasonCode, note, setNote }) => (
  <div className="space-y-2">
    <div className="space-y-1.5">
      <Label className="text-xs">Cancellation reason (required — audited)</Label>
      <Select value={reasonCode} onValueChange={setReasonCode}>
        <SelectTrigger data-testid={`${testPrefix}-reason-select`} className="h-9"><SelectValue placeholder="Why is this appointment cancelled?" /></SelectTrigger>
        <SelectContent>
          {cancelReasons.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs">Note {reasonCode === "other" ? "(required — describe the reason)" : "(optional)"}</Label>
      <Textarea data-testid={`${testPrefix}-note`} placeholder="Additional details…" value={note} onChange={(e) => setNote(e.target.value)} />
    </div>
  </div>
);

/* Full consultation interaction set for one appointment — outcome recording
   (occurrence + mandatory commercial outcome + note), reschedule, cancel,
   and manual Meet-link fallback. One instance per appointment row; shared
   between the standalone Consultations page and Customer 360's Consultations
   tab so both stay in sync with a single implementation. */
export const ConsultationActions = ({ appt, counsellors = [], onSaved, canAddMeetLink = true, compact = false, forceOpen = false, hideTriggerButtons = false }) => {
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [outcomeStatus, setOutcomeStatus] = useState("attended");
  const [outcomeNote, setOutcomeNote] = useState("");
  const [ocResched, setOcResched] = useState({ date: "", reason: "" });
  const [ocNextConsult, setOcNextConsult] = useState({ date: "" });
  const [ocCancelReasonCode, setOcCancelReasonCode] = useState("");
  const [ocCancelNote, setOcCancelNote] = useState("");
  const [ocSaving, setOcSaving] = useState(false);
  const [biz, setBiz] = useState({ outcome: "", next_action: "call", lost_reason: "", closure_initiated_by: "", followup_at: "", starting_stage: "", next_counsellor: "", followup_owner: "", product_code: "" });
  const [intentCat, setIntentCat] = useState("");
  const [savingIntent, setSavingIntent] = useState(false);
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  const [cancelReasons, setCancelReasons] = useState([]);
  useEffect(() => { api.get("/appointments/cancel-reasons").then((r) => setCancelReasons(r.data)).catch(() => {}); }, []);
  const childProducts = products.filter((p) => p.parent_code === appt.product_code);
  // 2026-09 round 4 — genuine CHILD products only, never the ambiguous
  // family root itself (e.g. STUDENT_VISA/SKILL_MIGRATION). The root used
  // to be prepended here; it's never actually a valid signup target
  // (services.unblocked_family_choices excludes it — a family root has no
  // Commercial Plan of its own — this was a frontend-only bug offering a
  // choice the backend would always 422 on if picked).
  const familyOptions = childProducts.filter((p) => !p.consultation_outcome_blocked);
  // 2026-09 Partnership Signup correction — product-config-driven, never
  // hardcoded by product code. A deferred-Signup product (e.g. Student
  // Visa Partnership) can only ever reach "Agreed to Proceed" here; its
  // canonical Signup is derived automatically later, server-side, once
  // admission + payment + Leadership approval are all complete.
  const apptProduct = products.find((p) => p.code === appt.product_code);
  const isDeferredSignup = !!apptProduct?.deferred_signup;
  const bizOutcomeOptions = isDeferredSignup
    ? [["agreed_to_proceed", "Agreed to Proceed"], ["not_ready", "Not Ready"], ["lost", "Lost"]]
    : [["signup", "Sign Up / Proceed"], ["not_ready", "Not Ready"], ["lost", "Lost"]];
  const isConversionOutcome = ["signup", "agreed_to_proceed"].includes(biz.outcome);
  // A consultation booked under a FAMILY root (e.g. STUDENT_VISA — the code
  // every real intake books under) never carries deferred_signup itself; only
  // the specific child picked below does (e.g. SA_PARTNERSHIP). The backend
  // decides against that RESOLVED child (routers_extras.record_outcome), so
  // "Sign Up / Proceed" + a deferred-Signup child must be sent as
  // "agreed_to_proceed" — sending "signup" is always refused (422) and would
  // leave no UI path to start a Partnership Case from its consultation.
  const selectedProduct = products.find((p) => p.code === biz.product_code);
  const effectiveOutcome = biz.outcome === "signup" && selectedProduct?.deferred_signup
    ? "agreed_to_proceed" : biz.outcome;
  // 2026-09 round 4 — never silently auto-select a child product server-
  // side-only. When exactly one child is currently valid, pre-fill it here
  // (a convenience — the employee still sees the field, populated, and
  // must submit with it visible) rather than leaving product_code empty
  // and letting the backend's own single-choice auto-pick happen with no
  // UI trace of it at all. Never runs when there's more than one choice —
  // that always requires a deliberate click.
  useEffect(() => {
    if (outcomeOpen && isConversionOutcome && familyOptions.length === 1 && !biz.product_code) {
      setBiz((b) => ({ ...b, product_code: familyOptions[0].code }));
    }
  }, [outcomeOpen, isConversionOutcome, familyOptions.length, familyOptions, biz.product_code]);

  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState("");
  const [reschedReason, setReschedReason] = useState("");

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReasonCode, setCancelReasonCode] = useState("");
  const [cancelNote, setCancelNote] = useState("");

  const [meetOpen, setMeetOpen] = useState(false);
  const [meetLink, setMeetLink] = useState("");

  // R15 U1: each slot belongs to the exact (appointment type, date) it was fetched for; changing either invalidates it at once, late responses are ignored, and the status says why
  // nothing can be picked (closed / not permitted / could not load). The server re-checks every slot and seat on submit.
  const slotLoader = (date) => async () => (await api.get(`/appointments/slots?appointment_type_id=${appt.appointment_type_id}&date_str=${date}`)).data;
  const reschedPicker = useSlotPicker({ key: reschedOpen && reschedDate ? `${appt.appointment_type_id}|${reschedDate}|${appt.appointment_id}` : null, date: reschedDate, load: slotLoader(reschedDate) });
  const ocReschedPicker = useSlotPicker({ key: outcomeOpen && ocResched.date ? `${appt.appointment_type_id}|${ocResched.date}|oc|${appt.appointment_id}` : null, date: ocResched.date, load: slotLoader(ocResched.date) });
  const ocNextPicker = useSlotPicker({ key: outcomeOpen && ocNextConsult.date ? `${appt.appointment_type_id}|${ocNextConsult.date}|next|${appt.appointment_id}` : null, date: ocNextConsult.date, load: slotLoader(ocNextConsult.date) });

  const openOutcome = () => {
    setOutcomeOpen(true);
    // 2026-09 R13 — default the occurrence to the appointment's OWN real
    // status rather than unconditionally "Attended": a cancelled/no-show
    // appointment's outcome form must reflect what actually happened, never
    // silently imply it was attended (live-audit-confirmed bug). Only
    // touched here (on open), never overwritten by a draft already in
    // progress — see resetOutcome/closeOutcome below.
    setOutcomeStatus(appt.status === "cancelled" ? "cancelled"
      : appt.status === "no_show" ? "no_show" : "attended");
    setIntentCat(INTENT_OPTIONS.includes(appt.intent) ? appt.intent : "");
  };
  // Lets a caller open straight to the outcome dialog without the user
  // clicking the Outcome button themselves — used when a stage-gate
  // blocker (required_consultation_outcome) routes here directly instead
  // of a generic override prompt. Reuses this exact dialog, not a copy.
  //
  // 2026-09 R13 (live-audit-confirmed bug — "popup cannot reliably
  // reopen"): the old effect depended on `forceOpen` alone, which every
  // caller passed as the LITERAL boolean `true` — an effect dependency
  // that never changes after mount fires exactly once, ever. Closing the
  // dialog without saving left no code path able to reopen it (only a full
  // page reload, which also discarded the draft note). `appt` is included
  // here because a PARENT blocker's `appt` is a freshly re-fetched object
  // on every new stage-move attempt (a genuinely new reference each time,
  // even for the identical appointment) — so a user re-clicking the same
  // stage button after closing without saving now correctly reopens the
  // form again, while the dialog being closed by resetOutcome (which never
  // touches `forceOpen`/`appt`) never spuriously reopens it.
  useEffect(() => { if (forceOpen) openOutcome(); }, [forceOpen, appt]); // eslint-disable-line react-hooks/exhaustive-deps
  // 2026-09 R13 — closing WITHOUT saving (Escape/overlay/Cancel-close)
  // must leave the draft note and in-progress selections intact so a
  // reopen (via the fix above, or the ordinary trigger button) resumes
  // where the user left off — only a genuinely SUCCESSFUL save clears the
  // form (resetOutcome below). The persisted stage/task state is already
  // untouched by a close-without-save (nothing was submitted) — this only
  // fixes the client-side draft being silently discarded too.
  const closeOutcome = () => { setOutcomeOpen(false); };
  const resetOutcome = () => {
    setOutcomeOpen(false); setOutcomeNote("");
    setBiz({ outcome: "", next_action: "call", lost_reason: "", closure_initiated_by: "", followup_at: "", starting_stage: "", next_counsellor: "", followup_owner: "", product_code: "" });
    setOcResched({ date: "", reason: "" });
    setOcNextConsult({ date: "" });
    setOcCancelReasonCode(""); setOcCancelNote("");
    setIntentCat("");
  };

  const saveIntent = async () => {
    if (!intentCat) return;
    setSavingIntent(true);
    try {
      await api.patch(`/opportunities/${appt.opportunity_id}/intent`, { category: intentCat });
      toast.success("Intent updated");
      onSaved?.();
    } catch (e) { toast.error(errDetail(e)); } finally { setSavingIntent(false); }
  };

  const saveOutcome = async () => {
    setOcSaving(true);
    try {
      if (outcomeStatus === "reschedule") {
        await api.post(`/appointments/${appt.appointment_id}/reschedule`,
          { start: ocReschedPicker.slot, reason: ocResched.reason.trim() || null });
        toast.success("Rescheduled — appears under Upcoming, history preserved");
        // 2026-09 R13 — `kind` (never a bare outcome value) tells a caller
        // like Customer360Page/PipelinesPage's blocker handler whether a
        // STAGE-QUALIFYING business outcome was actually just recorded —
        // a reschedule/cancel/no-show never is, so the caller must not
        // treat this as license to retry the originally-attempted stage
        // move (see the `isConversion` branch below).
        resetOutcome(); onSaved?.({ kind: "reschedule", isConversion: false }); return;
      }
      if (outcomeStatus === "cancelled") {
        await api.post(`/appointments/${appt.appointment_id}/cancel`,
          { reason_code: ocCancelReasonCode, note: ocCancelNote.trim() || null });
        toast.success("Cancelled — reason recorded");
        resetOutcome(); onSaved?.({ kind: "cancelled", isConversion: false }); return;
      }
      await api.patch(`/appointments/${appt.appointment_id}/outcome`, { status: outcomeStatus, note: outcomeNote });
      if (outcomeStatus === "no_show") {
        toast.success("Recorded: No Show — follow-up options preserved");
        resetOutcome(); onSaved?.({ kind: "no_show", isConversion: false }); return;
      }
      let followupAt = null;
      if (biz.outcome === "not_ready") {
        if (biz.next_action === "call") followupAt = biz.followup_at ? `${biz.followup_at}:00+05:30` : null;
        if (biz.next_action === "consultation") followupAt = ocNextPicker.slot;
      }
      const r = await api.post(`/consultations/${appt.appointment_id}/outcome`, {
        outcome: effectiveOutcome, feedback: outcomeNote,
        next_action: biz.outcome === "not_ready" ? biz.next_action : null,
        followup_at: followupAt,
        lost_reason: biz.outcome === "lost" ? biz.lost_reason : null,
        closure_initiated_by: biz.outcome === "lost" ? biz.closure_initiated_by : null,
        starting_stage: isConversionOutcome ? (biz.starting_stage || null) : null,
        product_code: isConversionOutcome ? (biz.product_code || null) : null,
        counsellor_user_id: biz.outcome === "not_ready" && biz.next_action === "consultation" ? (biz.next_counsellor || null) : null,
        followup_owner_user_id: biz.outcome === "not_ready" && biz.next_action === "call" ? (biz.followup_owner || null) : null,
      });
      const outcomeLabel = effectiveOutcome === "signup" ? "Sign Up — sent for leadership approval"
        : effectiveOutcome === "agreed_to_proceed" ? "Agreed to Proceed — admission journey started"
        : effectiveOutcome;
      toast.success(r.data.note || `Outcome recorded: ${outcomeLabel}`);
      resetOutcome();
      // 2026-09 R13 (live-audit-confirmed bug — "Not Ready authorises
      // Signup"): `isConversion` reflects the ACTUAL outcome just saved
      // (effectiveOutcome), never the stage the dialog originally opened
      // to pursue. A caller must only retry the originally-blocked stage
      // move when this is true — saving "Not Ready"/"Lost" here must never
      // resume a stale attempt toward Signup/Agreed to Proceed merely
      // because that's what the user first clicked.
      onSaved?.({ kind: "business_outcome", outcome: effectiveOutcome, isConversion: isConversionOutcome });
      // 2026-09 Workstream J Section 5 — the outcome save just reconciled this
      // customer's other open tasks server-side (task_reconciliation); any OTHER
      // currently-mounted component showing task/Next-Action state (not just this
      // dialog's own onSaved refetch) refreshes via the SAME event bus the review
      // prompt already uses, never a poll.
      notifyTaskReviewChanged();
      // Non-blocking: the outcome is saved and this dialog is already closed; the shared
      // panel (AppShell) lists any tasks SalesOS could not prove satisfied.
      maybeShowTaskReview(r.data.task_review);
    } catch (e) { toast.error(errDetail(e)); } finally { setOcSaving(false); }
  };

  const reschedGuard = useRef(false);        // R15 U1: a second click / Enter while the reschedule is in flight must not post it twice
  const saveReschedule = async () => {
    if (!reschedPicker.slot || reschedGuard.current) return;
    reschedGuard.current = true;
    try {
      await api.post(`/appointments/${appt.appointment_id}/reschedule`,
        { start: reschedPicker.slot, reason: reschedReason.trim() || null });
      toast.success("Rescheduled — history preserved");
      setReschedOpen(false); setReschedDate(""); setReschedReason("");
      onSaved?.();
    } catch (e) { toast.error(errDetail(e)); reschedPicker.reload(); } finally { reschedGuard.current = false; }
  };

  const saveCancel = async () => {
    try {
      await api.post(`/appointments/${appt.appointment_id}/cancel`,
        { reason_code: cancelReasonCode, note: cancelNote.trim() || null });
      toast.success("Appointment cancelled — slot released");
      setCancelOpen(false); setCancelReasonCode(""); setCancelNote("");
      onSaved?.();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const saveMeetLink = async () => {
    try {
      await api.patch(`/appointments/${appt.appointment_id}/meet-link`, { meet_link: meetLink });
      toast.success("Meet link saved (manual fallback — audited)");
      setMeetOpen(false); setMeetLink("");
      onSaved?.();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Meet link copied"); }
    catch { toast.error("Could not copy link"); }
  };

  const meetSlot = appt.meet_link ? (
    <div className="flex items-center gap-1">
      <a href={appt.meet_link} target="_blank" rel="noreferrer" data-testid={`ca-meet-join-${appt.appointment_id}`}
        className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--info))] hover:underline">
        <Video className="h-3.5 w-3.5" /> Join
      </a>
      <Button data-testid={`ca-meet-copy-${appt.appointment_id}`} variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => copyLink(appt.meet_link)}>
        <Copy className="h-3 w-3" />
      </Button>
    </div>
  ) : canAddMeetLink ? (
    <Button data-testid={`ca-meet-add-${appt.appointment_id}`} variant="outline" size="sm"
      className={compact ? "h-7 text-xs w-full" : "h-6 text-xs px-2"} onClick={() => { setMeetOpen(true); setMeetLink(""); }}>
      <Link2 className="h-3 w-3 mr-1" /> Add Meet link
    </Button>
  ) : compact ? <div /> : null;

  const btnCls = compact ? "h-7 text-xs w-full" : "h-7 text-xs";

  return (
    <>
      {!hideTriggerButtons && (
        <div className={compact ? "grid grid-cols-2 gap-1.5 w-56" : "flex items-center gap-1.5 flex-wrap"}>
          {meetSlot}
          <Button data-testid={`ca-outcome-${appt.appointment_id}`} size="sm" variant="outline" className={btnCls} onClick={openOutcome}>
            <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Outcome
          </Button>
          <Button data-testid={`ca-reschedule-${appt.appointment_id}`} size="sm" variant="outline" className={btnCls} onClick={() => { setReschedOpen(true); setReschedDate(""); setReschedReason(""); }}>
            <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Reschedule
          </Button>
          <Button data-testid={`ca-cancel-${appt.appointment_id}`} size="sm" variant="ghost" className={btnCls} onClick={() => { setCancelOpen(true); setCancelReasonCode(""); setCancelNote(""); }}>
            <CalendarX2 className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
        </div>
      )}

      {/* Consultation Outcome — ONE dialog, THREE sections */}
      <Dialog open={outcomeOpen} onOpenChange={(o) => { if (!o) closeOutcome(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Consultation outcome</DialogTitle></DialogHeader>
          {appt.opportunity_id && <WhatHappensNextPanel opportunityId={appt.opportunity_id} compact surface="outcome-form-consultation" />}
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/40 p-2.5 text-xs space-y-0.5">
              <p><b>{appt.contact_name}</b> · {appt.customer_id}</p>
              <p className="text-muted-foreground">{appt.appointment_type_name} · {fmtIST(appt.scheduled_start, { year: true })} · {appt.counsellor_name || "Unassigned"}</p>
            </div>

            <div className="space-y-1.5 rounded-md border p-2.5" data-testid="ca-tags-intent">
              {/* Opportunity tags are edited from Customer 360's own
                  canonical TagsPanel now — this dialog no longer carries a
                  second, redundant tag-editing surface for the same
                  opportunity (2026-09 Customer 360 control cleanup). */}
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Intent</Label>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Select value={intentCat} onValueChange={setIntentCat}>
                  <SelectTrigger data-testid="ca-intent-select" className="h-8 text-xs w-36"><SelectValue placeholder="Intent…" /></SelectTrigger>
                  <SelectContent>
                    {INTENT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="ca-intent-save" size="sm" variant="outline" className="h-8 text-xs"
                  disabled={!intentCat || intentCat === appt.intent || savingIntent} onClick={saveIntent}>
                  {savingIntent ? "Saving…" : "Update"}
                </Button>
                {appt.intent && <span className="text-[11px] text-muted-foreground">Current: {appt.intent.replace(/_/g, " ")}</span>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Section 1 — What happened to this appointment?</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {[["attended", "Attended"], ["no_show", "No Show"], ["reschedule", "Reschedule"], ["cancelled", "Cancelled"]].map(([v, l]) => (
                  <Button key={v} data-testid={`ca-occurrence-${v}-${appt.appointment_id}`} size="sm" variant={outcomeStatus === v ? "default" : "outline"} onClick={() => setOutcomeStatus(v)}>{l}</Button>
                ))}
              </div>
              {outcomeStatus === "attended" && <p className="text-[11px] text-muted-foreground">Appears under <b>Completed</b>. Section 2 — Commercial outcome is mandatory below.</p>}
              {outcomeStatus === "no_show" && <p className="text-[11px] text-muted-foreground">Appears under <b>No Show</b> — no commercial outcome required; follow-up automations preserved.</p>}
            </div>
            {outcomeStatus === "reschedule" && (
              <div className="space-y-2 rounded-md border p-2.5">
                <Label className="text-xs">New date &amp; time (original history preserved; appears under Upcoming)</Label>
                <Input data-testid="ca-occurrence-reschedule-date" type="date" value={ocResched.date} onChange={(e) => setOcResched({ ...ocResched, date: e.target.value })} />
                <SlotPickerView picker={ocReschedPicker} idPrefix="ca-occ-resched" compact gridClass="grid grid-cols-4 gap-1.5" />
                <Input data-testid="ca-occurrence-reschedule-reason" placeholder="Reason (optional)" value={ocResched.reason} onChange={(e) => setOcResched({ ...ocResched, reason: e.target.value })} />
              </div>
            )}
            {outcomeStatus === "cancelled" && (
              <div className="rounded-md border p-2.5">
                <CancelReasonFields testPrefix="ca-occurrence-cancel" cancelReasons={cancelReasons}
                  reasonCode={ocCancelReasonCode} setReasonCode={setOcCancelReasonCode}
                  note={ocCancelNote} setNote={setOcCancelNote} />
              </div>
            )}

            {outcomeStatus === "attended" && (
              <div className="space-y-2 rounded-md border border-primary/40 p-2.5" data-testid="ca-outcome-section-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Section 2 — Commercial outcome (mandatory)</Label>
                <div className="grid grid-cols-3 gap-1.5">
                  {bizOutcomeOptions.map(([v, l]) => (
                    <Button key={v} data-testid={`ca-biz-outcome-${v}-${appt.appointment_id}`} size="sm" variant={biz.outcome === v ? "default" : "outline"} onClick={() => setBiz({ ...biz, outcome: v })}>{l}</Button>
                  ))}
                </div>
                {isConversionOutcome && (
                  <div className="space-y-1.5 rounded-md border p-2.5">
                    <p className="text-[11px] text-muted-foreground">
                      {effectiveOutcome === "signup"
                        ? "Creates a Leadership Approval Request (customer, product, consultant, consultation date, your note). The Case activates only on approval — same Contact, no duplication."
                        : "Starts the admission journey now (a real Process Case, real stage work) — this is not a commercial signup. There is no Partnership Signup: Leadership approval is still required once admission is confirmed and the Visa fee is paid, but it advances the same Case directly, with no separate stage."}
                    </p>
                    {familyOptions.length >= 1 && (
                      <div className="space-y-1">
                        <Label className="text-xs">Specific product (required — confirm before saving)</Label>
                        <Select value={biz.product_code} onValueChange={(v) => setBiz({ ...biz, product_code: v })}>
                          <SelectTrigger data-testid="ca-biz-product-code"><SelectValue placeholder="Choose the specific product…" /></SelectTrigger>
                          <SelectContent>
                            {familyOptions.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {familyOptions.length === 1 && (
                          <p className="text-[11px] text-muted-foreground">Only one product is currently available for this family — confirm it's correct before saving.</p>
                        )}
                      </div>
                    )}
                    <Input data-testid="ca-biz-starting-stage" placeholder="Requested starting Case stage (optional, e.g. Onboarding)" value={biz.starting_stage} onChange={(e) => setBiz({ ...biz, starting_stage: e.target.value })} />
                  </div>
                )}
                {biz.outcome === "not_ready" && (
                  <div className="space-y-2 rounded-md border p-2.5">
                    <Label className="text-xs">Choose the next action (mandatory — Not Ready cannot be saved without a future action)</Label>
                    <Select value={biz.next_action} onValueChange={(v) => setBiz({ ...biz, next_action: v })}>
                      <SelectTrigger data-testid="ca-biz-next-action"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consultation">Schedule another consultation</SelectItem>
                        <SelectItem value="call">Schedule follow-up call</SelectItem>
                      </SelectContent>
                    </Select>
                    {biz.next_action === "call" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Follow-up call date &amp; time (goes to Calls → Follow-ups)</Label>
                        <Input data-testid="ca-biz-followup-at" type="datetime-local" value={biz.followup_at} onChange={(e) => setBiz({ ...biz, followup_at: e.target.value })} />
                        {counsellors.length > 0 && (
                          <Select value={biz.followup_owner || "me"} onValueChange={(v) => setBiz({ ...biz, followup_owner: v === "me" ? "" : v })}>
                            <SelectTrigger data-testid="ca-biz-followup-owner" className="h-8 text-xs"><SelectValue placeholder="Assigned to me" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="me">Assign to me</SelectItem>
                              {counsellors.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                    {biz.next_action === "consultation" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Next consultation date &amp; slot (appears under Upcoming)</Label>
                        <Input data-testid="ca-biz-nextconsult-date" type="date" value={ocNextConsult.date} onChange={(e) => setOcNextConsult({ ...ocNextConsult, date: e.target.value })} />
                        <SlotPickerView picker={ocNextPicker} idPrefix="ca-biz-next" compact gridClass="grid grid-cols-4 gap-1.5" />
                        {counsellors.length > 0 && (
                          <Select value={biz.next_counsellor || "same"} onValueChange={(v) => setBiz({ ...biz, next_counsellor: v === "same" ? "" : v })}>
                            <SelectTrigger data-testid="ca-biz-next-counsellor" className="h-8 text-xs"><SelectValue placeholder="Same consultant" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="same">Same consultant</SelectItem>
                              {counsellors.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {biz.outcome === "lost" && (
                  <>
                    <Input data-testid="ca-biz-lost-reason" placeholder="Lost reason (required — opportunity history preserved)" value={biz.lost_reason} onChange={(e) => setBiz({ ...biz, lost_reason: e.target.value })} />
                    <Select value={biz.closure_initiated_by} onValueChange={(v) => setBiz({ ...biz, closure_initiated_by: v })}>
                      <SelectTrigger data-testid="ca-biz-closure-initiated-by"><SelectValue placeholder="Who decided not to proceed? (required)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer decided not to proceed</SelectItem>
                        <SelectItem value="aussie_pathways">Aussie Pathways declined this customer</SelectItem>
                        <SelectItem value="mutual_other">Mutual / other</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
            )}

            {outcomeStatus !== "reschedule" && outcomeStatus !== "cancelled" && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Section 3 — Consultation note {outcomeStatus === "attended" && ["not_ready", "lost"].includes(biz.outcome) ? "(mandatory)" : ""}</Label>
                <Textarea data-testid="ca-outcome-note-input" placeholder="Note stored with author + timestamp; visible in consultation history, Customer 360 and the Case after signup…" value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid={`ca-outcome-save-${appt.appointment_id}`} disabled={ocSaving
              || (["attended", "no_show"].includes(outcomeStatus) && !outcomeNote.trim())
              || (outcomeStatus === "reschedule" && !ocReschedPicker.slot)
              || (outcomeStatus === "cancelled" && !ocCancelReasonCode)
              || (outcomeStatus === "cancelled" && ocCancelReasonCode === "other" && !ocCancelNote.trim())
              || (outcomeStatus === "attended" && !biz.outcome)
              || (outcomeStatus === "attended" && biz.outcome === "lost" && !biz.lost_reason.trim())
              || (outcomeStatus === "attended" && biz.outcome === "lost" && !biz.closure_initiated_by)
              || (outcomeStatus === "attended" && isConversionOutcome && familyOptions.length >= 1 && !biz.product_code)
              || (outcomeStatus === "attended" && biz.outcome === "not_ready" && biz.next_action === "consultation" && !ocNextPicker.slot)
              || (outcomeStatus === "attended" && biz.outcome === "not_ready" && biz.next_action === "call" && !biz.followup_at)}
              onClick={saveOutcome}>
              {ocSaving ? "Saving…" : outcomeStatus === "reschedule" ? "Confirm reschedule" : outcomeStatus === "cancelled" ? "Confirm cancellation" : "Save consultation outcome"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog (quick action, outside the outcome flow) */}
      <Dialog open={reschedOpen} onOpenChange={(o) => !o && setReschedOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Reschedule consultation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>New date</Label>
              <Input data-testid="ca-reschedule-date-input" type="date" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} />
            </div>
            <SlotPickerView picker={reschedPicker} idPrefix="ca-resched" gridClass="grid grid-cols-4 gap-1.5" />
            <Input data-testid="ca-reschedule-reason-input" placeholder="Reason (optional)" value={reschedReason} onChange={(e) => setReschedReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button data-testid={`ca-reschedule-confirm-${appt.appointment_id}`} disabled={!reschedPicker.slot} onClick={saveReschedule}>Confirm reschedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Meet link dialog (fallback while Calendar sync is off/failed) */}
      <Dialog open={meetOpen} onOpenChange={(o) => !o && setMeetOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add Meet link manually</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Calendar sync is not available for this appointment — paste a Google Meet link created manually. This is audited and shared in reminders once WhatsApp is live.</p>
            <Input data-testid="ca-meet-link-input" placeholder="https://meet.google.com/…" value={meetLink} onChange={(e) => setMeetLink(e.target.value)} />
          </div>
          <DialogFooter>
            <Button data-testid={`ca-meet-link-save-${appt.appointment_id}`} disabled={!meetLink.trim().startsWith("https://")} onClick={saveMeetLink}>Save link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog (quick action, outside the outcome flow) — a real
          confirmation step, never an immediate click-to-cancel. */}
      <Dialog open={cancelOpen} onOpenChange={(o) => !o && setCancelOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel this consultation?</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            {appt.contact_name} · {fmtIST(appt.scheduled_start, { year: true })}. This releases the slot and cannot be undone from here.
          </p>
          <CancelReasonFields testPrefix="ca-cancel" cancelReasons={cancelReasons}
            reasonCode={cancelReasonCode} setReasonCode={setCancelReasonCode}
            note={cancelNote} setNote={setCancelNote} />
          <DialogFooter>
            <Button data-testid={`ca-cancel-back-${appt.appointment_id}`} variant="ghost" onClick={() => setCancelOpen(false)}>Never mind</Button>
            <Button data-testid={`ca-cancel-confirm-${appt.appointment_id}`} variant="destructive"
              disabled={!cancelReasonCode || (cancelReasonCode === "other" && !cancelNote.trim())}
              onClick={saveCancel}>
              Confirm cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
